import { History, Loader2, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { PiiAction } from "@/api/types";
import { useAuth } from "@/auth/useAuth";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PromptHistoryDialog } from "@/components/ui/prompt-history-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useLlmModels } from "@/features/llms/hooks/useLlmModels";
import {
  usePiiConfig,
  usePiiDetections,
  usePiiPromptHistory,
  useRollbackPiiPrompt,
  useUpdatePiiConfig,
} from "@/features/pii/hooks/usePii";

const ALL_CATEGORIES = [
  "cpf",
  "cnpj",
  "email",
  "phone",
  "credit_card",
  "rg",
  "address",
  "name",
] as const;

export function PiiConfigPage() {
  const { t } = useTranslation("pii");
  const { isAdmin } = useAuth();
  const config = usePiiConfig();
  const update = useUpdatePiiConfig();
  const llmModels = useLlmModels();
  const detections = usePiiDetections({ limit: 100 });

  const [llmModelId, setLlmModelId] = useState<string>("");
  const [categories, setCategories] = useState<string[]>([]);
  const [action, setAction] = useState<PiiAction>("mask");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const history = usePiiPromptHistory(historyOpen);
  const rollback = useRollbackPiiPrompt();

  useEffect(() => {
    if (!config.data) return;
    setLlmModelId(config.data.llm_model_id ?? "");
    setCategories(config.data.detect_categories);
    setAction(config.data.action);
    setSystemPrompt(config.data.system_prompt ?? "");
  }, [config.data]);

  if (!isAdmin) {
    return (
      <div className="shadow-card rounded-2xl border border-line bg-surface p-6 text-sm text-ink-500">
        Acesso restrito a administradores.
      </div>
    );
  }
  if (config.isLoading) return <LoadingState />;
  if (config.isError)
    return <ErrorState error={config.error} onRetry={() => void config.refetch()} />;

  const toggleCategory = (cat: string) => {
    setCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat],
    );
  };

  async function save() {
    try {
      await update.mutateAsync({
        llm_model_id: llmModelId || null,
        detect_categories: categories,
        action,
        system_prompt: systemPrompt || null,
      });
      toast.success(t("config.saved"));
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-warning-500" /> {t("title")}
          </span>
        }
        description={t("subtitle")}
      />

      <Tabs defaultValue="config">
        <TabsList>
          <TabsTrigger value="config">{t("tabs.config")}</TabsTrigger>
          <TabsTrigger value="detections">{t("tabs.detections")}</TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>{t("config.llmModel")}</Label>
            <Select
              value={llmModelId || "none"}
              onValueChange={(v) => setLlmModelId(v === "none" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="(Nenhum -- so regex)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nenhum (so regex)</SelectItem>
                {llmModels.data?.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.display_name} ({m.provider})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-ink-500">{t("config.llmModelHint")}</p>
          </div>

          <div className="space-y-2">
            <Label>{t("config.categories")}</Label>
            <div className="flex flex-wrap gap-2">
              {ALL_CATEGORIES.map((cat) => {
                const active = categories.includes(cat);
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => toggleCategory(cat)}
                    className={
                      active
                        ? "rounded-full border border-brand-300 bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700"
                        : "rounded-full border border-line bg-surface px-3 py-1 text-xs text-ink-500 hover:border-brand-300 hover:text-brand-700"
                    }
                  >
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("config.action")}</Label>
            <Select value={action} onValueChange={(v) => setAction(v as PiiAction)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mask">{t("config.actions.mask")}</SelectItem>
                <SelectItem value="block">{t("config.actions.block")}</SelectItem>
                <SelectItem value="warn">{t("config.actions.warn")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label>{t("config.systemPrompt")}</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setHistoryOpen(true)}
              >
                <History className="h-3.5 w-3.5" />
                Histórico
              </Button>
            </div>
            <Textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={5}
              placeholder="(opcional -- override do prompt default do detector LLM)"
            />
          </div>

          <Button onClick={save} disabled={update.isPending}>
            {update.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            {t("config.save")}
          </Button>

          <PromptHistoryDialog
            resourceLabel="prompt de PII"
            versions={history.data ?? []}
            isLoading={history.isLoading}
            currentPrompt={systemPrompt}
            onRollback={(version) => rollback.mutateAsync({ version })}
            isRollingBack={rollback.isPending}
            open={historyOpen}
            onOpenChange={setHistoryOpen}
          />
        </TabsContent>

        <TabsContent value="detections" className="space-y-3 pt-4">
          {detections.isLoading ? <LoadingState /> : null}
          {detections.data && detections.data.length === 0 ? (
            <p className="text-sm text-ink-500">{t("detections.empty")}</p>
          ) : null}
          {detections.data && detections.data.length > 0 ? (
            <div className="shadow-card overflow-x-auto rounded-2xl border border-line bg-surface">
              <table className="w-full text-xs">
                <thead className="bg-canvas text-left text-[10px] uppercase tracking-wide text-ink-500">
                  <tr>
                    <th className="px-3 py-2.5 font-semibold">
                      {t("detections.columns.createdAt")}
                    </th>
                    <th className="px-3 py-2.5 font-semibold">
                      {t("detections.columns.category")}
                    </th>
                    <th className="px-3 py-2.5 font-semibold">
                      {t("detections.columns.direction")}
                    </th>
                    <th className="px-3 py-2.5 font-semibold">
                      {t("detections.columns.source")}
                    </th>
                    <th className="px-3 py-2.5 font-semibold">
                      {t("detections.columns.action")}
                    </th>
                    <th className="px-3 py-2.5 font-semibold">
                      {t("detections.columns.hint")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line text-ink-900">
                  {detections.data.map((d) => (
                    <tr key={d.id} className="hover:bg-canvas/60">
                      <td className="px-3 py-2">
                        {new Date(d.created_at).toLocaleString("pt-BR")}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline">{d.category}</Badge>
                      </td>
                      <td className="px-3 py-2">{d.direction}</td>
                      <td className="px-3 py-2">{d.source}</td>
                      <td className="px-3 py-2">{d.action}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-ink-700">
                        {d.value_hint ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
